import { useEffect, useState } from 'react';
import Spinner from '../../../../reusable/spinner/spinner.component';
import Client from '../../../../../tools/client';

import {
  Container,
  FieldGroup,
  FieldSection,
  Label,
  Input,
  Toggle,
  ButtonRow,
  Button,
  Description
} from '../../configuration-sections.styles';

const client = new Client();

const FooterConfigurationDisplay = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const fetchConfig = async () => {
      const res = await client.setup();
      const footer = res.configuration?.footer || {};
      setForm({
        title: footer.title?.content || '',
        titleActive: footer.title?.active === 'true' || footer.title?.active === true,
        text: footer.text?.content || '',
        textActive: footer.text?.active === 'true' || footer.text?.active === true,
        copyright: footer.copyright?.content || '',
        copyrightActive: footer.copyright?.active === 'true' || footer.copyright?.active === true,
        socialActive: footer.socials?.active === 'true' || footer.socials?.active === true,
      });
      setLoading(false);
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleToggle = (key) => {
    setForm({ ...form, [key]: !form[key] });
  };

  const handleSave = async () => {
    const mapping = {
      title: 'footer.title.content',
      titleActive: 'footer.title.active',
      text: 'footer.text.content',
      textActive: 'footer.text.active',
      copyright: 'footer.copyright.content',
      copyrightActive: 'footer.copyright.active',
      socialActive: 'footer.socials.active'
    };

    for (const [key, value] of Object.entries(form)) {
      const configKey = mapping[key];
      if (configKey) {
        await client.updateConfigurationByKey(configKey, {
          value: typeof value === 'boolean' ? String(value) : value,
        });
      }
    }
    setEditing(false);
  };

  if (loading) return <Spinner />;

  return (
    <Container>
      <FieldSection>
        <FieldGroup>
          <Label htmlFor="title">Footer Title</Label>
          <Description>
            A short heading that appears above the footer text—commonly used for disclaimers or notes.
          </Description>
          <Input
            name="title"
            value={form.title}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label>Show Title</Label>
          <Description>Enable or disable the footer title visibility.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.titleActive}
              onChange={() => handleToggle('titleActive')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>
      </FieldSection>

      <FieldSection>
        <FieldGroup>
          <Label htmlFor="text">Footer Text</Label>
          <Description>
            A longer message placed in the footer—typically used for legal disclaimers, terms, or policy summaries.
          </Description>
          <Input
            name="text"
            value={form.text}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>

        <FieldGroup>
          <Label>Show Text</Label>
          <Description>Toggle visibility of the footer text.</Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.textActive}
              onChange={() => handleToggle('textActive')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>
      </FieldSection>

      <FieldSection>
        <FieldGroup>
          <Label>Show Copyright</Label>
          <Description>
            Enables a copyright notice at the bottom of the footer. Often used for "© Your Company 2025" text.
          </Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.copyrightActive}
              onChange={() => handleToggle('copyrightActive')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="copyright">Custom Copyright Text</Label>
          <Description>
            If provided, this message will be used instead of the default © notice.
          </Description>
          <Input
            name="copyright"
            value={form.copyright}
            onChange={handleChange}
            disabled={!editing}
          />
        </FieldGroup>
      </FieldSection>

      <FieldSection>
        <FieldGroup>
          <Label>Show Social Icons</Label>
          <Description>
            Displays social media icons (from company config) in the footer when enabled.
          </Description>
          <Toggle>
            <input
              type="checkbox"
              checked={form.socialActive}
              onChange={() => handleToggle('socialActive')}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>
      </FieldSection>

      <ButtonRow>
        {editing ? (
          <>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button primary onClick={handleSave}>Save</Button>
          </>
        ) : (
          <Button primary onClick={() => setEditing(true)}>Edit</Button>
        )}
      </ButtonRow>
    </Container>
  );
};

export default FooterConfigurationDisplay;